import { PitchListHelper } from 'classes/helpers/pitch-list.helper';
import IMG_DIRT from 'components/common/trajectory-view/img/dirt_dark.png';
import IMG_GRASS from 'components/common/trajectory-view/img/grass_dark.png';
import GREEN_RECTANGLE from 'components/common/trajectory-view/img/green_rect.png';
import IMG_SKY from 'components/common/trajectory-view/img/nighttime.png';
import IMG_STRIKEZONE from 'components/common/trajectory-view/img/strike_zone3d_bw.png';
import { format } from 'date-fns-tz';
import {
  LINE_WIDTH,
  POINT_OFFSETS,
  RPM_TO_RADPS,
  STRIKEZONE_PLATE_DISTANCE_FT,
} from 'enums/traj-visualizer';
import { ITVDefinition } from 'interfaces/traj-visualizer/i-definition';
import {
  GROUP_ACTUAL,
  GROUP_AVG_ROTATED,
  GROUP_ROTATED,
  GROUP_TARGET,
} from 'interfaces/traj-visualizer/i-group-definition';
import { ITVSceneBall } from 'interfaces/traj-visualizer/i-scene-ball';
import { ITVSummary } from 'interfaces/traj-visualizer/i-summary';
import { AimingHelper } from 'lib_ts/classes/aiming.helper';
import { BallHelper } from 'lib_ts/classes/ball.helper';
import { EllipseHelper } from 'lib_ts/classes/ellipse.helper';
import { DEG_TO_RAD } from 'lib_ts/classes/math.utilities';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import {
  LOCAL_DATETIME_FORMAT,
  LOCAL_TIMEZONE,
} from 'lib_ts/enums/datetime.enums';
import { BuildPriority } from 'lib_ts/enums/pitches.enums';
import { IMachineState } from 'lib_ts/interfaces/i-machine-state';
import { IStrikeZone } from 'lib_ts/interfaces/i-strike-zone';
import { IBallState, ITrajectory } from 'lib_ts/interfaces/pitches';
import { IAziAltCoordinate, IPosition } from 'lib_ts/interfaces/pitches/i-base';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import { clone } from 'lodash';
import {
  BackSide,
  BoxGeometry,
  BufferGeometry,
  Color,
  Euler,
  Event,
  Group,
  Line,
  LineBasicMaterial,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  ObjectLoader,
  Quaternion,
  RepeatWrapping,
  TextureLoader,
  Vector3,
} from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { v4 } from 'uuid';

export class TVHelper {
  /** computes next rotation state based on current ball orientation, returning undefined if results are invalid */
  static getNextOrientation = (config: {
    delta: number;
    speed: number;
    ball: Group;
    bs: IBallState;
  }) => {
    const netSpin = BallHelper.getNetSpin(config.bs);

    if (!isNaN(netSpin) && netSpin >= 1e-5) {
      const axis = new Vector3(
        config.bs.wx / netSpin,
        config.bs.wy / netSpin,
        config.bs.wz / netSpin
      );

      const oDelta = new Quaternion();
      oDelta.setFromAxisAngle(
        axis,
        /** set a larger multiplier to spin the ball faster */
        config.delta * config.speed * netSpin * RPM_TO_RADPS
      );

      const oEuler = new Euler(
        config.ball.rotation.x,
        config.ball.rotation.y,
        config.ball.rotation.z,
        'XYZ'
      );

      const qCurrent = new Quaternion();
      qCurrent.setFromEuler(oEuler);

      const qNext = new Quaternion();
      qNext.multiplyQuaternions(oDelta, qCurrent);

      return new Euler().setFromQuaternion(qNext);
    } else {
      return undefined;
    }
  };

  /** output is using THREE coordinates (i.e. z represents distance between pitcher and home) */
  static positionAtTime = (t: number, traj: ITrajectory): IPosition => {
    return {
      px: traj.px + traj.vx * t + traj.ax * t * t * 0.5,
      py: traj.py + traj.vy * t + traj.ay * t * t * 0.5,
      pz: traj.pz - traj.vz * t + traj.az * t * t * 0.5,
    };
  };

  static getSummary = (config: {
    bs: IBallState;
    traj: ITrajectory;
    ms: IMachineState;
    shots: IMachineShot[];
    releaseFt: number;

    includeTarget?: boolean;
    includeAvgRotated?: boolean;
    includeEachActual?: boolean;
    includeEachRotated?: boolean;
  }): Partial<ITVSummary> => {
    const output: Partial<ITVSummary> = {};

    if (config.includeTarget) {
      output.target = {
        guid: v4(),
        description: 'Target trajectory of the pitch',
        button_group: GROUP_TARGET,
        color: GROUP_TARGET.color,
        traj: config.traj,
        bs: config.bs,
      };
    }

    if (config.includeEachActual) {
      const eachActual: ITVDefinition[] = [];

      config.shots.forEach((s, i) => {
        const timestamp = format(new Date(s._created), LOCAL_DATETIME_FORMAT, {
          timeZone: LOCAL_TIMEZONE,
        });

        eachActual.push({
          guid: v4(),
          description: `Actual trajectory of training shot ${
            i + 1
          } @ ${timestamp}`,
          button_group: GROUP_ACTUAL,
          color: GROUP_ACTUAL.color,
          traj: s.traj,
          bs: s.bs ?? config.bs,
        });
      });

      output.actualTrajs = eachActual;
    }

    const rotatedShots = EllipseHelper.getRotatedShots({
      ms: config.ms,
      shots: config.shots,
      releaseFt: config.releaseFt,
    });

    const avgRotated = PitchListHelper.getAvgShot(rotatedShots);

    if (config.includeAvgRotated && avgRotated.traj) {
      output.avgRotated = {
        guid: 'average',
        description: 'Average of rotated trajectories from training shots',
        button_group: GROUP_AVG_ROTATED,
        // darker green
        color: GROUP_AVG_ROTATED.color,
        traj: avgRotated.traj,
        bs: avgRotated.bs ?? config.bs,
      };
    }

    if (config.includeEachRotated && avgRotated.actual_ms) {
      const eachRotated: ITVDefinition[] = [];

      const aimed = AimingHelper.aimWithShots({
        ms: config.ms,
        bs: config.bs,
        traj: config.traj,
        seams: undefined,
        breaks: undefined,
        priority: BuildPriority.Spins,
        aimingShots: config.shots,
        releaseFt: config.releaseFt,
        targetPlate: TrajHelper.getPlateLoc(config.traj),
        stepSize: undefined,
        clampTilt: false,
        training: false,
        checkConfidence: false,
      });

      if (aimed) {
        const refTilt = aimed.ms.tilt;
        const refYaw = aimed.ms.yaw;

        config.shots.forEach((s, i) => {
          const delta: IAziAltCoordinate = {
            altitude_rad: (refTilt - s.target_ms.tilt) * DEG_TO_RAD,
            azimuth_rad: (refYaw - s.target_ms.yaw) * DEG_TO_RAD,
          };

          const timestamp = format(
            new Date(s._created),
            LOCAL_DATETIME_FORMAT,
            {
              timeZone: LOCAL_TIMEZONE,
            }
          );

          const rotated = TrajHelper.rotateTraj(s.traj, delta);

          eachRotated.push({
            guid: v4(),
            description: `Rotated trajectory of training shot ${
              i + 1
            } @ ${timestamp}`,
            button_group: GROUP_ROTATED,
            // lighter green
            color: GROUP_ROTATED.color,
            traj: rotated,
            bs: s.bs ?? config.bs,
          });
        });

        output.rotatedTrajs = eachRotated;
      }
    }

    return output;
  };

  // adjust ball position and spin relative to the next point in time
  static moveBall = (config: {
    ball: ITVSceneBall;
    deltaSec: number; // changes depending on the speed factor
    animationSpin: boolean;
    animationSpeed: number;
    verbose?: boolean;
  }) => {
    const { ball, deltaSec, animationSpin, animationSpeed, verbose } = config;

    const adjDeltaSec = deltaSec * config.animationSpeed;

    const { group, definition, localTraj } = ball;

    if (animationSpin) {
      /** ball will keep rotating regardless of whether flight is complete or not */
      const oNext = this.getNextOrientation({
        delta: adjDeltaSec,
        /** scale the speed by a larger number to make it spin faster */
        speed: animationSpeed * 0.05,
        ball: group,
        bs: definition.bs,
      });

      if (oNext) {
        group.rotation.x = oNext.x;
        group.rotation.y = oNext.y;
        group.rotation.z = oNext.z;
      }
    }

    if (ball.timestampSec >= ball.arrivalSec) {
      // nothing more to do
      if (verbose) {
        console.debug('**** exiting because arrived');
      }
      return;
    }

    const nextTimestampSec = Math.min(
      ball.timestampSec + adjDeltaSec,
      ball.arrivalSec
    );

    if (verbose) {
      console.debug('**** got next time stamp', {
        next: nextTimestampSec,
        fromAdjDelta: ball.timestampSec + adjDeltaSec,
        fromArrival: ball.arrivalSec,
      });
    }

    const nextPos = this.positionAtTime(nextTimestampSec, localTraj);

    // update ball position
    group.position.x = nextPos.px;
    group.position.y = nextPos.py;
    group.position.z = nextPos.pz;

    // draw line segment to the next position
    ball.points[0].push(clone(group.position));

    // update the timestamp at the end
    ball.timestampSec = nextTimestampSec;
  };

  static traceBall = (ball: ITVSceneBall, scene: Object3D<Event>) => {
    /** remove the old lines */
    ball.lines.forEach((line) => scene.remove(line));

    /** compute new lines  */
    const newLines = POINT_OFFSETS.map((offset) => {
      const offsetPoints = ball.points[0].map((p) => p.clone().add(offset));
      const geometry = new BufferGeometry().setFromPoints(offsetPoints);
      const material = new LineBasicMaterial({
        linewidth: LINE_WIDTH,
        color: ball.definition.color,
      });
      return new Line(geometry, material);
    });

    /** update reference */
    ball.lines = newLines;

    /** add lines to scene */
    newLines.forEach((line) => scene.add(line));
  };

  static addBall = async (
    definition: ITVDefinition,
    scene: Object3D<Event>
  ) => {
    const ball = await this.loadBall(scene);

    const output: ITVSceneBall = {
      definition: definition,
      group: ball,

      timestampSec: 0,
      arrivalSec: TrajHelper.getFlightTimeSec(definition.traj),

      /** needs to be updated so it cannot share a reference with input traj (from props) */
      localTraj: clone(definition.traj),

      localBs: clone(definition.bs),

      points: POINT_OFFSETS.map(() => []),
      lines: POINT_OFFSETS.map(() => new Line()),
    };

    /** update defaults */
    output.group.rotation.x = 0;
    output.group.rotation.y = 0;
    output.group.rotation.z = 0;

    output.group.scale.x = 1;
    output.group.scale.y = 1;
    output.group.scale.z = 1;

    /** starting position is the release position */
    output.group.position.x = definition.traj.px;
    output.group.position.y = definition.traj.py;
    output.group.position.z = definition.traj.pz;

    return output;
  };

  /** loads ball, adds it to scene, returns the ball */
  static loadBall = (scene: Object3D<Event>) => {
    const gLoader = new GLTFLoader();

    return new Promise<Group>((resolve) => {
      gLoader.load(
        process.env.PUBLIC_URL + '/glb/smallest_ball.glb',
        (gltf) => {
          gltf.scene.traverse((child) => {
            if (child instanceof Mesh) {
              child.material.emissive = new Color(0.35, 0.35, 0.35);
              child.material.emissiveIntensity = 0.35;
              child.castShadow = true;
              child.receiveShadow = true;
            }
          });

          scene.add(gltf.scene);

          resolve(gltf.scene);
        },
        undefined,
        (error) => {
          console.error(error);
        }
      );
    });
  };

  /** loads strike zone, adds it to scene */
  static addStrikeZone = (scene: Object3D<Event>, strikeZone: IStrikeZone) => {
    const tLoader = new TextureLoader();

    return new Promise<void>((resolve) => {
      const widthFt =
        Math.max(strikeZone.left_ft, strikeZone.right_ft) -
        Math.min(strikeZone.left_ft, strikeZone.right_ft);

      const heightFt = strikeZone.top_ft - strikeZone.bottom_ft;

      // in feet
      const szGeometry = new BoxGeometry(widthFt, heightFt, 0.01);

      const sztexture = tLoader.load(IMG_STRIKEZONE);
      const greentexture = tLoader.load(GREEN_RECTANGLE);
      const szMaterial = [
        new MeshBasicMaterial({
          map: greentexture,
          opacity: 0.0,
          transparent: true,
        }),
        new MeshBasicMaterial({
          map: greentexture,
          opacity: 0.0,
          transparent: true,
        }),
        new MeshBasicMaterial({
          map: greentexture,
          opacity: 0.0,
          transparent: true,
        }),
        new MeshBasicMaterial({
          map: greentexture,
          opacity: 0.0,
          transparent: true,
        }),
        new MeshBasicMaterial({
          map: sztexture,
          opacity: 0.5,
          transparent: true,
        }),
        new MeshBasicMaterial({
          map: sztexture,
          opacity: 0.5,
          transparent: true,
        }),
      ];

      const szRect = new Mesh(szGeometry, szMaterial);

      szRect.scale.y = 1;

      // middle of the strike zone from ground
      szRect.position.y = strikeZone.plate_ft.plate_z;

      // anchored to home plate
      szRect.position.z = STRIKEZONE_PLATE_DISTANCE_FT;

      scene.add(szRect);

      resolve();
    });
  };

  // * any prevents a bunch of ts errors
  static addTextures = (scene: any) => {
    try {
      const tLoader = new TextureLoader();

      const skyTexture = tLoader.load(IMG_SKY);
      const skyMaterial = new MeshBasicMaterial({
        map: skyTexture,
        side: BackSide,
      });
      // const fenceTexture = tLoader.load(fenceImg);
      // fenceTexture.wrapS = RepeatWrapping;
      // fenceTexture.wrapT = RepeatWrapping;
      // fenceTexture.repeat.set(50, 1);
      // const fenceMaterial = new MeshBasicMaterial({
      //   map: fenceTexture,
      //   side: BackSide,
      // });
      const dirtTexture = tLoader.load(IMG_DIRT);
      dirtTexture.wrapS = RepeatWrapping;
      dirtTexture.wrapT = RepeatWrapping;
      dirtTexture.repeat.set(5, 5);
      const dirtMaterial = new MeshBasicMaterial({
        map: dirtTexture,
      });
      const dirtTextureBack = tLoader.load(IMG_DIRT);
      dirtTextureBack.wrapS = RepeatWrapping;
      dirtTextureBack.wrapT = RepeatWrapping;
      dirtTextureBack.repeat.set(5, 5);
      const dirtMaterialBack = new MeshBasicMaterial({
        map: dirtTextureBack,
        side: BackSide,
      });
      const dirtTexture2 = tLoader.load(IMG_DIRT);
      dirtTexture2.wrapS = RepeatWrapping;
      dirtTexture2.wrapT = RepeatWrapping;
      dirtTexture2.repeat.set(1, 10);
      const dirtMaterial2 = new MeshBasicMaterial({
        map: dirtTexture2,
      });
      const grassTexture = tLoader.load(IMG_GRASS);
      grassTexture.wrapS = RepeatWrapping;
      grassTexture.wrapT = RepeatWrapping;
      grassTexture.repeat.set(40, 40);
      const grassMaterial = new MeshBasicMaterial({
        map: grassTexture,
      });
      const skydome = scene.getObjectByName('SkyDome');
      skydome.material = skyMaterial;
      // skydome.material.color = new Color(0x5959ff);
      // const fence = scene.getObjectByName('Fence');
      // fence.material = fenceMaterial;
      // fence.material.color = new Color(0x444444);
      const grass = scene.getObjectByName('Grass');
      grass.material = grassMaterial;
      const pitcherMound = scene.getObjectByName('PitcherMound');
      pitcherMound.material = dirtMaterial;
      const firstBaseMound = scene.getObjectByName('FirstBaseMound');
      firstBaseMound.material = dirtMaterialBack;
      const secondBaseMound = scene.getObjectByName('SecondBaseMound');
      secondBaseMound.material = dirtMaterialBack;
      const thirdBaseMound = scene.getObjectByName('ThirdBaseMound');
      thirdBaseMound.material = dirtMaterialBack;
      const dirtFirstToSecond = scene.getObjectByName('DirtFirstToSecond');
      dirtFirstToSecond.material = dirtMaterial;
      const dirtSecondToThird = scene.getObjectByName('DirtSecondToThird');
      dirtSecondToThird.material = dirtMaterial;
      const outfield = scene.getObjectByName('OutfieldDirt');
      outfield.material = dirtMaterial;
      const dirtNearFirst = scene.getObjectByName('DirtPatchNearFirst');
      dirtNearFirst.material = dirtMaterial;
      const dirtNearThird = scene.getObjectByName('DirtPatchNearThird');
      dirtNearThird.material = dirtMaterial;
      const battersMound = scene.getObjectByName('BattersMound');
      battersMound.material = dirtMaterial;
      const dirtToFirst = scene.getObjectByName('DirtToFirst');
      dirtToFirst.material = dirtMaterial2;
      const dirtToThird = scene.getObjectByName('DirtToThird');
      dirtToThird.material = dirtMaterial2;
      const plateSquare = scene.getObjectByName('PlateSquare');
      plateSquare.material.color = new Color(0xffffff);
      const plateTip = scene.getObjectByName('PlateTip');
      plateTip.material.color = new Color(0xffffff);
      const lathe = scene.getObjectByName('Lathe');
      // scene.remove(fence);
      scene.remove(lathe);
    } catch (e) {
      console.error(e);
      console.error('TRAJ VIEW: failed to add scene textures');
    }
  };

  static createScene = () => {
    const oLoader = new ObjectLoader();

    return new Promise<Object3D<Event> | undefined>((resolve) => {
      oLoader.load(
        process.env.PUBLIC_URL + '/json/scene_flat_colors_5.json',
        (result) => {
          resolve(result);
        },
        undefined,
        (error) => {
          console.error(error);
          resolve(undefined);
        }
      );
    });
  };
}
